import { useSDK } from "~hooks";
import { Order } from "~graphql/sdk";
import { useEffect, useState } from "react";
import {
  broadcastParamsFromReportingId,
  getBroadcastParamsFromQuery,
} from "~hooks/broadcastHelper";
import { useRouter } from "next/router";

const BroadcastReportingData = "BroadcastReportingData";

const getReportingData = () => {
  const dataString = localStorage?.getItem(BroadcastReportingData);
  if (dataString) {
    return JSON.parse(dataString) as string[];
  }
  return [];
};

const setReportingData = (data: string[] = []) => {
  localStorage?.removeItem(BroadcastReportingData);
  localStorage?.setItem(BroadcastReportingData, JSON.stringify(data));
};

export const useCampaignReportingOpen = (trigger: boolean) => {
  const router = useRouter();
  const sdk = useSDK();
  const [
    removeCampaignCacheOnWindowClose,
    setRemoveCampaignCacheOnWindowClose,
  ] = useState(false);
  const { query } = router;
  const {
    broadcastId,
    userId,
    waitlistUserId,
    sourceId,
    reportingId,
    transactionalType,
  } = getBroadcastParamsFromQuery(query);

  const useMembership = transactionalType === "membership";

  useEffect(() => {
    if (typeof window === "undefined" || !removeCampaignCacheOnWindowClose) {
      return;
    }
    const handleBeforeUnload = () => {
      // delete the reporting data for this campaign-id when the browser is closed.
      const data = getReportingData();
      setReportingData(data.filter((id) => id != reportingId));
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [typeof window, removeCampaignCacheOnWindowClose]);

  useEffect(() => {
    if (trigger) {
      if (broadcastId && (userId || waitlistUserId) && sourceId) {
        const doAsyncReporting = async () => {
          try {
            const data = getReportingData();
            if (!data.includes(reportingId)) {
              await sdk.broadcastReportingLinkOpened({
                input: {
                  broadcastId,
                  ...(useMembership
                    ? { membershipId: sourceId }
                    : { eventId: sourceId }),
                  userId,
                  waitlistUserId,
                },
              });
              const newData = [...data, reportingId];
              setReportingData(newData);
              setRemoveCampaignCacheOnWindowClose(true);
            }
          } catch (error) {
            // Handle errors
            console.error("Error saving data:", error);
          }
        };

        void doAsyncReporting();
      }
    }
  }, [trigger]);
};

export const useCampaignReportingForOrder = async (order: Order) => {
  const data = getReportingData();
  const reportingId = data.find((reportingId) => {
    const { sourceId } = broadcastParamsFromReportingId(reportingId);
    return order.event.id == sourceId;
  });
  if (reportingId) {
    const {
      broadcastId,
      userId,
      waitlistUserId,
      sourceId,
    } = broadcastParamsFromReportingId(reportingId);

    const sdk = useSDK();
    await sdk.broadcastReportingOrderCreated({
      input: {
        broadcastId,
        eventId: sourceId,
        userId,
        waitlistUserId,
        orderId: order.id,
      },
    });
  }
};
